// src/components/admin/payroll/hooks/usePayrollEmployees.js
// Custom hook for paginated employee data with validation status

import { useState, useEffect, useCallback, useRef } from 'react';
import payrollService from '../../../../services/payroll.service';

// Map frontend camelCase status to backend snake_case
const statusMapping = {
    'autoValidated': 'auto_validated',
    'needsReview': 'needs_review',
    'hasIssues': 'has_issues',
    'all': 'all'
};

const usePayrollEmployees = (period, initialFilters = {}) => {
    const [employees, setEmployees] = useState([]);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 50,
        total: 0,
        totalPages: 0,
        hasMore: false
    });
    const [filters, setFilters] = useState({
        status: 'all',
        department: null,
        search: null,
        ...initialFilters
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Track previous request to avoid race conditions
    const requestIdRef = useRef(0);

    // Fetch employees from backend
    const fetchEmployees = useCallback(async (page = 1, newFilters = null) => {
        if (!period) return;

        const currentRequestId = ++requestIdRef.current;
        setLoading(true);
        setError(null);

        const activeFilters = newFilters || filters;

        try {
            // Map frontend status to backend format
            const backendStatus = statusMapping[activeFilters.status] || activeFilters.status || 'all';

            const result = await payrollService.getPaginatedEmployeesForPayroll(period, {
                page,
                limit: pagination.limit,
                status: backendStatus,
                department: activeFilters.department,
                search: activeFilters.search
            });

            // Only update if this is still the latest request
            if (currentRequestId !== requestIdRef.current) return;

            if (result.success && result.data) {
                // Ensure employees is always an array
                const employeeData = Array.isArray(result.data.employees)
                    ? result.data.employees
                    : [];
                setEmployees(employeeData);
                setPagination(prev => ({
                    ...prev,
                    ...(result.data.pagination || {})
                }));
            } else {
                setEmployees([]); // Reset to empty array on error
                setError(result.error || 'Failed to fetch employees');
            }
        } catch (err) {
            if (currentRequestId !== requestIdRef.current) return;
            console.error('Error fetching employees:', err);
            setEmployees([]); // Reset to empty array on error
            setError(err.message);
        } finally {
            if (currentRequestId === requestIdRef.current) {
                setLoading(false);
            }
        }
    }, [period, filters, pagination.limit]);

    // Initial fetch and refetch on period/filter changes
    useEffect(() => {
        fetchEmployees(1);
    }, [period, filters.status, filters.department]);

    // Debounced search
    useEffect(() => {
        const timer = setTimeout(() => {
            if (filters.search !== undefined) {
                fetchEmployees(1);
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [filters.search]);

    // Page change handler
    const setPage = useCallback((newPage) => {
        setPagination(prev => ({ ...prev, page: newPage }));
        fetchEmployees(newPage);
    }, [fetchEmployees]);

    // Filter change handler
    const updateFilters = useCallback((newFilters) => {
        setFilters(prev => ({ ...prev, ...newFilters }));
        // Reset to page 1 when filters change
        setPagination(prev => ({ ...prev, page: 1 }));
    }, []);

    // Search handler
    const setSearch = useCallback((searchTerm) => {
        setFilters(prev => ({ ...prev, search: searchTerm || null }));
    }, []);

    // Refetch current page
    const refetch = useCallback(() => {
        fetchEmployees(pagination.page);
    }, [fetchEmployees, pagination.page]);

    // Update single employee in local state (optimistic update)
    const updateEmployee = useCallback((employeeId, updates) => {
        setEmployees(prev => prev.map(emp =>
            emp.id === employeeId ? { ...emp, ...updates } : emp
        ));
    }, []);

    // Update multiple employees (optimistic update)
    const updateEmployees = useCallback((employeeIds, updates) => {
        setEmployees(prev => prev.map(emp =>
            employeeIds.includes(emp.id) ? { ...emp, ...updates } : emp
        ));
    }, []);

    return {
        employees,
        pagination,
        filters,
        loading,
        error,
        setPage,
        setFilters: updateFilters,
        setSearch,
        refetch,
        updateEmployee,
        updateEmployees
    };
};

export default usePayrollEmployees;
